import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trophy, Medal, Award, Calendar, ChevronDown, ChevronRight, Target, Users, Swords } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Standings({ user }) {
  const navigate = useNavigate();
  const [standings, setStandings] = useState([]);
  const [breakdownData, setBreakdownData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [registeredPlayers, setRegisteredPlayers] = useState([]);
  const [player1, setPlayer1] = useState('');
  const [player2, setPlayer2] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('all');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('all'); // 'all', 'mine', 'breakdown', or 'compare'
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [expandedPlayers, setExpandedPlayers] = useState({});
  const [expandedDivisions, setExpandedDivisions] = useState({});

  const fetchAvailableYears = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/standings/years`);
      setAvailableYears(response.data.years);
      setSelectedYear(response.data.current_year);
    } catch (error) {
      setSelectedYear(new Date().getFullYear());
    }
  }, []);

  const fetchRegisteredPlayers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/standings/players`);
      setRegisteredPlayers(response.data.players);
    } catch (error) {
      // Silent fail
    }
  }, []);

  const fetchStandings = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = viewMode === 'mine' 
        ? `${API}/standings/me/results?year=${selectedYear}`
        : `${API}/standings?year=${selectedYear}`;
      const response = await axios.get(endpoint);
      setStandings(response.data);
    } catch (error) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedYear]);

  const fetchBreakdown = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/standings/breakdown?year=${selectedYear}`);
      setBreakdownData(response.data);
    } catch (error) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  const fetchComparison = async () => {
    if (!player1 || !player2) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/standings/compare?player1=${encodeURIComponent(player1)}&player2=${encodeURIComponent(player2)}&year=${selectedYear}`);
      setCompareData(response.data);
    } catch (error) {
      setCompareData(null);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch on mount
  useEffect(() => {
    fetchAvailableYears();
    fetchRegisteredPlayers();
  }, [fetchAvailableYears, fetchRegisteredPlayers]);

  // Fetch data when viewMode or selectedYear changes
  useEffect(() => {
    if (selectedYear !== null) {
      if (viewMode === 'breakdown') {
        fetchBreakdown();
      } else if (viewMode === 'compare') {
        // Don't auto-fetch compare, wait for player selection
        setLoading(false);
      } else {
        fetchStandings();
      }
    }
  }, [viewMode, selectedYear, fetchBreakdown, fetchStandings]);

  const togglePlayerExpanded = (playerKey) => {
    setExpandedPlayers(prev => ({
      ...prev,
      [playerKey]: !prev[playerKey]
    }));
  };

  const toggleDivisionExpanded = (division) => {
    setExpandedDivisions(prev => ({
      ...prev,
      [division]: !prev[division]
    }));
  };

  const filteredStandings = selectedDivision === 'all' 
    ? standings 
    : standings.filter(s => s.division === selectedDivision);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-amber-600" strokeWidth={2} />;
    if (rank === 2) return <Medal className="w-5 h-5 text-stone-400" strokeWidth={2} />;
    if (rank === 3) return <Award className="w-5 h-5 text-amber-800" strokeWidth={2} />;
    return null;
  };

  const getRankClass = (rank) => {
    if (rank === 1) return 'rank-1 border-l-4 border-amber-600';
    if (rank === 2) return 'rank-2 border-l-4 border-stone-400';
    if (rank === 3) return 'rank-3 border-l-4 border-amber-800';
    return '';
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <p className="font-barlow text-xl text-slate-900">Loading standings...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-7xl mx-auto p-4 md:p-8 lg:p-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="font-barlow font-bold text-4xl md:text-5xl text-slate-900 uppercase tracking-tight mb-2">
                Standings {selectedYear}
              </h1>
              <p className="font-inter text-stone-600">Competition leaderboard across all divisions</p>
            </div>

            {/* Controls */}
            <div className="flex flex-wrap gap-2 items-center">
              {/* Year Selector */}
              {availableYears.length > 0 && (
                <Select value={selectedYear?.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
                  <SelectTrigger className="w-32 bg-white border-stone-300 rounded-sm font-barlow" data-testid="year-selector">
                    <Calendar className="w-4 h-4 mr-2 text-stone-500" />
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableYears.map(year => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* View Mode Toggle */}
              <Button
                data-testid="view-all-button"
                onClick={() => setViewMode('all')}
                className={`font-barlow font-semibold uppercase tracking-wide text-sm rounded-sm ${
                  viewMode === 'all' 
                    ? 'bg-slate-900 text-white hover:bg-slate-800' 
                    : 'bg-white border border-stone-300 text-slate-700 hover:bg-stone-50'
                }`}
              >
                All Standings
              </Button>
              <Button
                data-testid="view-mine-button"
                onClick={() => setViewMode('mine')}
                className={`font-barlow font-semibold uppercase tracking-wide text-sm rounded-sm ${
                  viewMode === 'mine' 
                    ? 'bg-slate-900 text-white hover:bg-slate-800' 
                    : 'bg-white border border-stone-300 text-slate-700 hover:bg-stone-50'
                }`}
              >
                My Results
              </Button>
              <Button
                data-testid="view-breakdown-button"
                onClick={() => setViewMode('breakdown')}
                className={`font-barlow font-semibold uppercase tracking-wide text-sm rounded-sm ${
                  viewMode === 'breakdown' 
                    ? 'bg-slate-900 text-white hover:bg-slate-800' 
                    : 'bg-white border border-stone-300 text-slate-700 hover:bg-stone-50'
                }`}
              >
                Match Breakdown
              </Button>
              <Button
                data-testid="view-compare-button"
                onClick={() => { setViewMode('compare'); setCompareData(null); }}
                className={`font-barlow font-semibold uppercase tracking-wide text-sm rounded-sm ${
                  viewMode === 'compare' 
                    ? 'bg-slate-900 text-white hover:bg-slate-800' 
                    : 'bg-white border border-stone-300 text-slate-700 hover:bg-stone-50'
                }`}
              >
                <Swords className="w-4 h-4 mr-1 inline" />
                Compare
              </Button>
            </div>
          </div>

          {/* Division Filter - only show for standings views */}
          {(viewMode === 'all' || viewMode === 'mine') && (
            <div className="w-full md:w-64">
              <Select value={selectedDivision} onValueChange={setSelectedDivision}>
                <SelectTrigger data-testid="division-filter" className="bg-white border-stone-300 rounded-sm h-12 font-inter">
                  <SelectValue placeholder="All Divisions" />
                </SelectTrigger>
                <SelectContent className="bg-white border-stone-200 rounded-sm">
                  <SelectItem value="all">All Divisions</SelectItem>
                  {standings.map(s => (
                    <SelectItem key={s.division} value={s.division}>{s.division}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Compare Players View */}
        {viewMode === 'compare' && (
          <div className="space-y-6">
            {/* Player Selection */}
            <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6">
              <h2 className="font-barlow font-bold text-xl text-slate-900 uppercase tracking-tight mb-4">
                <Swords className="w-5 h-5 inline mr-2" />
                Head-to-Head Comparison
              </h2>
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1">
                  <label className="block font-inter text-sm text-stone-600 mb-2">Player 1</label>
                  <Select value={player1} onValueChange={setPlayer1}>
                    <SelectTrigger data-testid="player1-select" className="bg-white border-stone-300 rounded-sm h-12 font-inter">
                      <SelectValue placeholder="Select player..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-stone-200 rounded-sm">
                      {registeredPlayers.filter(p => p.name !== player2).map(p => (
                        <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-center">
                  <span className="font-barlow font-bold text-xl text-stone-400">VS</span>
                </div>
                <div className="flex-1">
                  <label className="block font-inter text-sm text-stone-600 mb-2">Player 2</label>
                  <Select value={player2} onValueChange={setPlayer2}>
                    <SelectTrigger data-testid="player2-select" className="bg-white border-stone-300 rounded-sm h-12 font-inter">
                      <SelectValue placeholder="Select player..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-stone-200 rounded-sm">
                      {registeredPlayers.filter(p => p.name !== player1).map(p => (
                        <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  data-testid="compare-button"
                  onClick={fetchComparison}
                  disabled={!player1 || !player2 || loading}
                  className="font-barlow font-semibold uppercase tracking-wide text-sm rounded-sm bg-amber-600 text-white hover:bg-amber-700 disabled:bg-stone-300 h-12 px-6"
                >
                  Compare
                </Button>
              </div>
            </div>

            {/* Comparison Results */}
            {compareData && (
              <div className="space-y-6">
                {/* Overall Summary Card */}
                <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
                  <div className="bg-slate-900 text-white px-6 py-4">
                    <h3 className="font-barlow font-bold text-2xl uppercase tracking-tight text-center">
                      Overall Record
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-center gap-8">
                      <div className="text-center">
                        <p className="font-inter text-sm text-stone-600 mb-1">{compareData.player1_name}</p>
                        <p className={`font-barlow font-bold text-4xl ${compareData.overall_player1_wins > compareData.overall_player2_wins ? 'text-green-600' : compareData.overall_player1_wins < compareData.overall_player2_wins ? 'text-red-600' : 'text-slate-900'}`}>
                          {compareData.overall_player1_wins}
                        </p>
                        <p className="font-inter text-xs text-stone-500 uppercase">Wins</p>
                      </div>
                      <div className="text-center">
                        <p className="font-barlow font-bold text-2xl text-stone-400">-</p>
                        {compareData.overall_ties > 0 && (
                          <p className="font-inter text-xs text-stone-500">{compareData.overall_ties} ties</p>
                        )}
                      </div>
                      <div className="text-center">
                        <p className="font-inter text-sm text-stone-600 mb-1">{compareData.player2_name}</p>
                        <p className={`font-barlow font-bold text-4xl ${compareData.overall_player2_wins > compareData.overall_player1_wins ? 'text-green-600' : compareData.overall_player2_wins < compareData.overall_player1_wins ? 'text-red-600' : 'text-slate-900'}`}>
                          {compareData.overall_player2_wins}
                        </p>
                        <p className="font-inter text-xs text-stone-500 uppercase">Wins</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Per-Division Breakdown */}
                {compareData.divisions.length === 0 ? (
                  <div className="bg-white border border-stone-200 rounded-sm p-12 text-center shadow-sm">
                    <Users className="w-16 h-16 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
                    <p className="font-barlow text-xl text-slate-900 mb-2">No shared matches</p>
                    <p className="font-inter text-stone-600">These players haven&apos;t competed together in {selectedYear}</p>
                  </div>
                ) : (
                  compareData.divisions.map(divSummary => {
                    const isExpanded = expandedDivisions[divSummary.division];
                    return (
                      <div key={divSummary.division} className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
                        {/* Division Header */}
                        <div 
                          className="bg-stone-100 border-b border-stone-200 px-6 py-4 cursor-pointer hover:bg-stone-200 transition-colors"
                          onClick={() => toggleDivisionExpanded(divSummary.division)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {isExpanded ? <ChevronDown className="w-5 h-5 text-stone-500" /> : <ChevronRight className="w-5 h-5 text-stone-500" />}
                              <h3 className="font-barlow font-bold text-xl text-slate-900 uppercase tracking-tight">
                                {divSummary.division}
                              </h3>
                              <span className="font-inter text-sm text-stone-500">({divSummary.total_matches} matches)</span>
                            </div>
                            <div className="flex items-center gap-6">
                              <div className="text-center">
                                <span className={`font-barlow font-bold text-lg ${divSummary.player1_wins > divSummary.player2_wins ? 'text-green-600' : 'text-slate-700'}`}>
                                  {divSummary.player1_wins}
                                </span>
                              </div>
                              <span className="text-stone-400">-</span>
                              <div className="text-center">
                                <span className={`font-barlow font-bold text-lg ${divSummary.player2_wins > divSummary.player1_wins ? 'text-green-600' : 'text-slate-700'}`}>
                                  {divSummary.player2_wins}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Stats Summary */}
                        <div className="grid grid-cols-2 divide-x divide-stone-100 border-b border-stone-100">
                          <div className="p-4 text-center">
                            <p className="font-inter text-xs text-stone-500 uppercase mb-1">{compareData.player1_name}</p>
                            <p className="font-mono text-sm text-slate-900">{divSummary.player1_total_points} pts</p>
                            <p className="font-inter text-xs text-stone-500">Avg: {divSummary.player1_avg_score}</p>
                          </div>
                          <div className="p-4 text-center">
                            <p className="font-inter text-xs text-stone-500 uppercase mb-1">{compareData.player2_name}</p>
                            <p className="font-mono text-sm text-slate-900">{divSummary.player2_total_points} pts</p>
                            <p className="font-inter text-xs text-stone-500">Avg: {divSummary.player2_avg_score}</p>
                          </div>
                        </div>

                        {/* Match Details (Expanded) */}
                        {isExpanded && (
                          <div className="bg-stone-50 p-4">
                            <table className="w-full">
                              <thead>
                                <tr className="text-left">
                                  <th className="py-2 px-2 font-barlow font-bold text-xs uppercase tracking-wider text-stone-500">Date</th>
                                  <th className="py-2 px-2 font-barlow font-bold text-xs uppercase tracking-wider text-stone-500">Match</th>
                                  <th className="py-2 px-2 text-center font-barlow font-bold text-xs uppercase tracking-wider text-stone-500">{compareData.player1_name.split(',')[0]}</th>
                                  <th className="py-2 px-2 text-center font-barlow font-bold text-xs uppercase tracking-wider text-stone-500">{compareData.player2_name.split(',')[0]}</th>
                                  <th className="py-2 px-2 text-center font-barlow font-bold text-xs uppercase tracking-wider text-stone-500">Winner</th>
                                </tr>
                              </thead>
                              <tbody>
                                {divSummary.matches.map((match) => (
                                  <tr key={`${match.match_date}-${match.file_name}`} className="border-t border-stone-200">
                                    <td className="py-2 px-2 font-inter text-sm text-slate-700">
                                      {match.match_date ? new Date(match.match_date).toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td className="py-2 px-2 font-inter text-sm text-slate-700 max-w-xs truncate" title={match.file_name}>
                                      {match.file_name.length > 25 ? match.file_name.substring(0, 25) + '...' : match.file_name}
                                    </td>
                                    <td className={`py-2 px-2 text-center font-mono text-sm ${match.winner === 'player1' ? 'text-green-600 font-bold' : 'text-slate-700'}`}>
                                      {match.player1_score} <span className="text-xs text-stone-400">(#{match.player1_rank})</span>
                                    </td>
                                    <td className={`py-2 px-2 text-center font-mono text-sm ${match.winner === 'player2' ? 'text-green-600 font-bold' : 'text-slate-700'}`}>
                                      {match.player2_score} <span className="text-xs text-stone-400">(#{match.player2_rank})</span>
                                    </td>
                                    <td className="py-2 px-2 text-center">
                                      {match.winner === 'player1' && <Trophy className="w-4 h-4 text-amber-600 inline" />}
                                      {match.winner === 'player2' && <Trophy className="w-4 h-4 text-amber-600 inline" />}
                                      {match.winner === 'tie' && <span className="text-stone-400 text-sm">Tie</span>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* No comparison yet */}
            {!compareData && !loading && (
              <div className="bg-white border border-stone-200 rounded-sm p-12 text-center shadow-sm">
                <Swords className="w-16 h-16 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
                <p className="font-barlow text-xl text-slate-900 mb-2">Select two players</p>
                <p className="font-inter text-stone-600">Choose players above to see their head-to-head comparison</p>
              </div>
            )}
          </div>
        )}

        {/* Match Breakdown View */}
        {viewMode === 'breakdown' && breakdownData && (
          <div className="space-y-6">
            {breakdownData.divisions.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-sm p-12 text-center shadow-sm">
                <Target className="w-16 h-16 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
                <p className="font-barlow text-xl text-slate-900 mb-2">No match data yet</p>
                <p className="font-inter text-stone-600">Upload scorecards to see match breakdowns</p>
              </div>
            ) : (
              breakdownData.divisions.map(division => {
                const divisionPlayers = breakdownData.players.filter(p => p.division === division);
                return (
                  <div key={division} className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
                    {/* Division Header */}
                    <div className="bg-stone-100 border-b border-stone-200 px-6 py-4">
                      <h2 className="font-barlow font-bold text-2xl text-slate-900 uppercase tracking-tight">
                        {division} - Match Breakdown
                      </h2>
                    </div>

                    {/* Players */}
                    <div className="divide-y divide-stone-100">
                      {divisionPlayers.map((player, idx) => {
                        const playerKey = `${player.player_name}-${player.division}`;
                        const isExpanded = expandedPlayers[playerKey];
                        
                        return (
                          <div key={playerKey} data-testid={`breakdown-player-${idx}`}>
                            {/* Player Summary Row */}
                            <div 
                              className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-stone-50 transition-colors"
                              onClick={() => togglePlayerExpanded(playerKey)}
                            >
                              <div className="flex items-center gap-4">
                                <button className="text-stone-500 hover:text-slate-900">
                                  {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                                </button>
                                <div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/player/${encodeURIComponent(player.player_name)}`);
                                    }}
                                    className="font-inter font-medium text-slate-900 hover:text-emerald-600 hover:underline transition-colors text-left"
                                  >
                                    {player.player_name}
                                  </button>
                                  <p className="font-inter text-sm text-stone-500">{player.rounds_played} matches</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-6">
                                <div className="text-right">
                                  <p className="font-inter text-xs text-stone-500 uppercase">Total Score</p>
                                  <p className="font-mono text-sm text-slate-900">{player.total_score}</p>
                                </div>
                                <div className="text-right">
                                  <span className="inline-block bg-amber-600 text-white font-barlow font-bold text-sm px-3 py-1 rounded-sm">
                                    {player.total_points} pts
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Expanded Match Details */}
                            {isExpanded && (
                              <div className="bg-stone-50 px-6 py-4 border-t border-stone-100">
                                <table className="w-full">
                                  <thead>
                                    <tr className="text-left">
                                      <th className="py-2 px-2 font-barlow font-bold text-xs uppercase tracking-wider text-stone-500">Date</th>
                                      <th className="py-2 px-2 font-barlow font-bold text-xs uppercase tracking-wider text-stone-500">Match</th>
                                      <th className="py-2 px-2 text-center font-barlow font-bold text-xs uppercase tracking-wider text-stone-500">Score</th>
                                      <th className="py-2 px-2 text-center font-barlow font-bold text-xs uppercase tracking-wider text-stone-500">Rank</th>
                                      <th className="py-2 px-2 text-center font-barlow font-bold text-xs uppercase tracking-wider text-stone-500">Points</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {player.matches.map((match, matchIdx) => (
                                      <tr key={matchIdx} className="border-t border-stone-200" data-testid={`match-row-${matchIdx}`}>
                                        <td className="py-2 px-2 font-inter text-sm text-slate-700">
                                          {match.match_date ? new Date(match.match_date).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td className="py-2 px-2 font-inter text-sm text-slate-700 max-w-xs truncate" title={match.file_name}>
                                          {match.file_name.length > 30 ? match.file_name.substring(0, 30) + '...' : match.file_name}
                                        </td>
                                        <td className="py-2 px-2 text-center font-mono text-sm text-slate-900">{match.score}</td>
                                        <td className="py-2 px-2 text-center">
                                          <span className={`font-barlow font-bold text-sm ${match.rank === 1 ? 'text-amber-600' : match.rank === 2 ? 'text-stone-500' : match.rank === 3 ? 'text-amber-800' : 'text-slate-700'}`}>
                                            #{match.rank}
                                            <span className="text-stone-400 font-normal text-xs">/{match.total_participants}</span>
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 text-center">
                                          <span className={`inline-block font-barlow font-bold text-sm px-2 py-0.5 rounded-sm ${match.points_earned > 0 ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                                            +{match.points_earned}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Standings Tables - only show for 'all' and 'mine' views */}
        {(viewMode === 'all' || viewMode === 'mine') && (filteredStandings.length === 0 ? (
          <div className="bg-white border border-stone-200 rounded-sm p-12 text-center shadow-sm">
            <Trophy className="w-16 h-16 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-barlow text-xl text-slate-900 mb-2">No standings yet</p>
            <p className="font-inter text-stone-600">Upload scorecards to see the leaderboard</p>
          </div>
        ) : (
          <div className="space-y-8">
            {filteredStandings.map(divisionData => (
              <div key={divisionData.division} className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
                {/* Division Header */}
                <div className="bg-stone-100 border-b border-stone-200 px-6 py-4">
                  <h2 className="font-barlow font-bold text-2xl text-slate-900 uppercase tracking-tight">
                    Division {divisionData.division}
                  </h2>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-stone-100 border-b border-stone-200">
                        <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Rank</th>
                        <th className="text-left py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Player</th>
                        <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Score</th>
                        <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Points</th>
                        <th className="text-center py-3 px-4 font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Rounds</th>
                      </tr>
                    </thead>
                    <tbody>
                      {divisionData.standings.map(entry => (
                        <tr 
                          key={`${entry.player_name}-${entry.rank}`} 
                          data-testid={`standing-row-${entry.rank}`}
                          className={`border-b border-stone-100 hover:bg-stone-50 transition-colors ${getRankClass(entry.rank)}`}
                        >
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              {getRankIcon(entry.rank)}
                              <span className="font-barlow font-bold text-lg text-slate-900">{entry.rank}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 font-inter text-sm text-slate-700 font-medium">
                            <button
                              onClick={() => navigate(`/player/${encodeURIComponent(entry.player_name)}`)}
                              className="text-left hover:text-emerald-600 hover:underline transition-colors"
                              data-testid={`player-link-${entry.player_name}`}
                            >
                              {entry.player_name}
                            </button>
                          </td>
                          <td className="py-4 px-4 text-center font-mono text-sm text-slate-900 font-medium">{entry.total_score}</td>
                          <td className="py-4 px-4 text-center">
                            <span className="inline-block bg-amber-600 text-white font-barlow font-bold text-sm px-3 py-1 rounded-sm">
                              {entry.total_points}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center font-inter text-sm text-slate-700">{entry.rounds_played}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Layout>
  );
}